// Experience / Updates & Gallery data.
//
// Structure is intentionally CMS-friendly so it can be swapped to a real backend
// later (e.g. /api/experience/:slug returning the same JSON shape).
//
// Slugs mirror TRUST_BADGES.slug. New categories: add a key here AND a badge in
// content.js with the same slug.
//
// Each entry: { id, date (ISO), title, description, images: [url], tags: [string] }

export const CATEGORY_META = {
  "doe-vendor": {
    title: "DOE Vendor",
    eyebrow: "NYC Department of Education",
    description:
      "Updates from our DOE-vendor catering programs — school events, staff appreciations, and student-focused service.",
    accent: "teal",
  },
  "nyc-vendor": {
    title: "NYC Vendor",
    eyebrow: "City Agency Catering",
    description:
      "Highlights from city-agency meetings, workshops, hearings, and program lunches we&rsquo;ve catered across the boroughs.",
    accent: "teal",
  },
  "mwbe-certified": {
    title: "MWBE Certified",
    eyebrow: "Minority & Women-Owned Business",
    description:
      "Our MWBE-certified work — partnerships, community programs, and supplier-diversity events that align with our mission.",
    accent: "gold",
  },
  "queens-based": {
    title: "Queens-Based",
    eyebrow: "Rooted in Jamaica, Queens",
    description:
      "Local Queens stories — community partners, neighborhood events, and the people behind Fresh Fast Foods.",
    accent: "green",
  },
  "workforce-development": {
    title: "Workforce Development",
    eyebrow: "SYEP & Community Hiring",
    description:
      "Staff development moments — SYEP students, interns, culinary-school grads, and our growing team.",
    accent: "gold",
  },
  "restaurant-catering": {
    title: "Restaurant + Catering",
    eyebrow: "BTWNYC Quick-Service Operator",
    description:
      "Behind-the-scenes from our BTWNYC quick-service operation and the catering events it powers — from breakfast trays to game-day spreads.",
    accent: "teal",
  },
};

// Per-category entries.
// `restaurant-catering` is populated heavily as requested; others get
// representative starter entries so the experience flow feels complete.
export const EXPERIENCE_ENTRIES = {
  "restaurant-catering": [
    {
      id: "rc-2025-09-team-shift",
      date: "2025-09-18",
      title: "Branded Team on Shift",
      description:
        "Our line crew running a Friday lunch service — branded F3 / BTWNYC gear, hot ticket times, and the consistency that makes catering re-orders easy.",
      images: [
        "https://static.prod-images.emergentagent.com/jobs/9c8ef01f-de5d-404e-b131-b692bd44c30d/images/f48eddb4cdae1d1260c5b8fba9be4968032ee24d8649ddd4fc7e9a63c4b278e7.png",
      ],
      tags: ["team", "operations"],
    },
    {
      id: "rc-2025-08-prep-line",
      date: "2025-08-22",
      title: "Behind the Catering Prep Line",
      description:
        "Trays portioned, labeled, and rolling out the door. Our kitchen prep flow — built from real restaurant systems, not just one-off catering.",
      images: [
        "https://img1.wsimg.com/isteam/ip/f73297d8-4b43-481b-a2c1-3a058376af76/IMG_6DAC737D2809-1.jpeg",
      ],
      tags: ["prep", "operations"],
    },
    {
      id: "rc-2025-07-staff-training",
      date: "2025-07-14",
      title: "Catering Staff Training Day",
      description:
        "A summer training session covering plating, packaging, and on-site setup — making sure every event runs the same way, every time.",
      images: [
        "https://img1.wsimg.com/isteam/ip/f73297d8-4b43-481b-a2c1-3a058376af76/IMG_D894B71550BA-1.jpeg",
      ],
      tags: ["training", "team"],
    },
    {
      id: "rc-2025-06-community-event",
      date: "2025-06-08",
      title: "Saturday Community Event",
      description:
        "A community-program lunch on a Saturday in Queens — wings, sliders, fruit drinks, and the energy you'd expect from BTW.",
      images: [
        "https://img1.wsimg.com/isteam/ip/f73297d8-4b43-481b-a2c1-3a058376af76/IMG_7179.jpg",
      ],
      tags: ["community", "event"],
    },
    {
      id: "rc-2025-05-kitchen-crew",
      date: "2025-05-19",
      title: "Kitchen Crew, Mid-Service",
      description:
        "A peek inside the kitchen during peak catering prep — multiple orders, multiple service styles, one team.",
      images: [
        "https://img1.wsimg.com/isteam/ip/f73297d8-4b43-481b-a2c1-3a058376af76/IMG_7165.jpg",
      ],
      tags: ["operations", "team"],
    },
    {
      id: "rc-2025-04-pack-out",
      date: "2025-04-25",
      title: "Pack-Out Before Delivery",
      description:
        "Labeled trays, branded packaging, and a checklist that goes out with every order. Setup-ready before the van even leaves Jamaica.",
      images: [
        "https://img1.wsimg.com/isteam/ip/f73297d8-4b43-481b-a2c1-3a058376af76/IMG_7167.jpg",
      ],
      tags: ["operations", "packaging"],
    },
    {
      id: "rc-2025-03-event-setup",
      date: "2025-03-12",
      title: "On-Site Event Setup",
      description:
        "First-thing-in-the-morning setup for an agency breakfast — bagels, coffee, fresh fruit, ready before the room fills up.",
      images: [
        "https://img1.wsimg.com/isteam/ip/f73297d8-4b43-481b-a2c1-3a058376af76/IMG_7168.jpg",
      ],
      tags: ["event", "setup"],
    },
  ],

  "workforce-development": [
    {
      id: "wd-2025-08-syep-cohort",
      date: "2025-08-15",
      title: "Summer SYEP Cohort Wrap-Up",
      description:
        "Closing day for our Summer Youth Employment Program cohort. Six weeks of front-of-house, prep, and catering ops experience.",
      images: [
        "https://img1.wsimg.com/isteam/ip/f73297d8-4b43-481b-a2c1-3a058376af76/IMG_D894B71550BA-1.jpeg",
      ],
      tags: ["SYEP", "training"],
    },
    {
      id: "wd-2025-06-interns",
      date: "2025-06-05",
      title: "Welcoming Our Summer Interns",
      description:
        "New interns started this week — pairing each with a senior team member for on-the-line mentorship.",
      images: [
        "https://img1.wsimg.com/isteam/ip/f73297d8-4b43-481b-a2c1-3a058376af76/IMG_7165.jpg",
      ],
      tags: ["interns", "mentorship"],
    },
  ],

  "queens-based": [
    {
      id: "qb-2025-07-community-day",
      date: "2025-07-04",
      title: "Jamaica Community Day",
      description:
        "We catered a community block event right in Jamaica — hometown energy, hometown food.",
      images: [
        "https://img1.wsimg.com/isteam/ip/f73297d8-4b43-481b-a2c1-3a058376af76/IMG_7179.jpg",
      ],
      tags: ["community", "queens"],
    },
  ],

  "mwbe-certified": [
    {
      id: "mw-2025-05-supplier-diversity",
      date: "2025-05-20",
      title: "Supplier Diversity Showcase",
      description:
        "Catered a corporate supplier-diversity event highlighting MWBE-certified vendors across NYC.",
      images: [
        "https://img1.wsimg.com/isteam/ip/f73297d8-4b43-481b-a2c1-3a058376af76/IMG_7167.jpg",
      ],
      tags: ["MWBE", "corporate"],
    },
  ],

  "nyc-vendor": [
    {
      id: "nv-2025-04-agency-workshop",
      date: "2025-04-10",
      title: "City Agency All-Day Workshop",
      description:
        "Breakfast trays, mid-morning fruit and coffee refresh, and individually packaged lunches for a full-day agency workshop.",
      images: [
        "https://img1.wsimg.com/isteam/ip/f73297d8-4b43-481b-a2c1-3a058376af76/IMG_6DAC737D2809-1.jpeg",
      ],
      tags: ["agency", "workshop"],
    },
  ],

  "doe-vendor": [
    {
      id: "doe-2025-03-staff-appreciation",
      date: "2025-03-22",
      title: "Staff Appreciation Breakfast",
      description:
        "A DOE-school staff appreciation breakfast — bagels, eggs, breakfast potatoes, and boxes of coffee, set up and ready before homeroom.",
      images: [
        "https://img1.wsimg.com/isteam/ip/f73297d8-4b43-481b-a2c1-3a058376af76/IMG_7168.jpg",
      ],
      tags: ["DOE", "school"],
    },
  ],
};

// CMS-ready accessor. Returns local data synchronously by category slug.
// The Gallery/Experience pages now hydrate this from the backend API
// (`GET /api/experiences`) at runtime — see useExperiences() hook below — so
// these static entries are only used as fallback when the network is unavailable.
export const getCategory = (slug) => {
  const meta = CATEGORY_META[slug];
  if (!meta) return null;
  const entries = (EXPERIENCE_ENTRIES[slug] || []).slice().sort((a, b) => {
    return new Date(b.date).getTime() - new Date(a.date).getTime();
  });
  return { slug, ...meta, entries };
};

// Build a getCategory-compatible payload from a flat list returned by the API.
export const buildCategory = (slug, apiEntries) => {
  const meta = CATEGORY_META[slug];
  if (!meta) return null;
  const entries = (apiEntries || [])
    .filter((e) => e.category === slug)
    .slice()
    .sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());
  return { slug, ...meta, entries };
};
